/**
 * AdminIBCLeads — Master-admin view of every Wealth for Life Blueprint
 * submission. Auto-refreshes. Shows tier distribution, full history per
 * lead, editable status / notes / next-action.
 *
 * Gated behind /dashboard/wealth-for-life — same auth as the other admin
 * routes (Protected → require_advisor). Master admin sees every lead.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "sonner";
import {
  Users, Bank, TrendUp, CurrencyDollar, MagnifyingGlass, ClockCounterClockwise,
  PaperPlaneTilt, CaretRight, Sparkle,
} from "@phosphor-icons/react";

const TIER_META = {
  t1: { label: "Working Wealth",   range: "$40K–$150K",  color: "#8C6E37" },
  t2: { label: "Rising Pro",       range: "$180K–$250K", color: "#A6884A" },
  t3: { label: "Established Pro",  range: "$280K–$450K", color: "#C5A059" },
  t4: { label: "Executive Class",  range: "$500K–$750K", color: "#D9BB6D" },
  t5: { label: "Capital Owner",    range: "$780K+",      color: "#EED690" },
};

const STATUS_OPTS = ["new", "contacted", "booked", "closed-won", "closed-lost"];

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString()}`);

export default function AdminIBCLeads() {
  const [data, setData] = useState({ leads: [], count: 0, total_submissions: 0, by_tier: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/ibc/leads");
      setData(r.data);
      if (r.data?.leads?.length && !selected) setSelected(r.data.leads[0]);
    } catch (e) {
      toast.error("Failed to load Wealth for Life leads");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return data.leads;
    return data.leads.filter((l) => {
      const rec = l.recommendation || {};
      return [
        l.email, l.name, rec.tier_label,
        rec.inputs?.primary_goal, l.status, l.advisor_notes,
      ].filter(Boolean).some((x) => String(x).toLowerCase().includes(needle));
    });
  }, [data.leads, q]);

  const saveField = async (field, value) => {
    if (!selected) return;
    try {
      await api.patch(`/admin/ibc/leads/${selected.id}`, { [field]: value });
      const updated = { ...selected, [field]: value };
      setSelected(updated);
      setData((d) => ({ ...d, leads: d.leads.map((l) => (l.id === updated.id ? updated : l)) }));
      toast.success(`Updated ${field}`);
    } catch {
      toast.error(`Failed to update ${field}`);
    }
  };

  return (
    <DashboardLayout>
      <div data-testid="admin-ibc-leads" className="p-6 space-y-6">
        {/* Header */}
        <header className="flex flex-wrap gap-3 items-start justify-between">
          <div>
            <p className="text-exodus-gold-deep text-xs tracking-[0.24em] uppercase font-semibold">Master Admin</p>
            <h1 className="mt-2 text-3xl font-bold text-exodus-gold flex items-center gap-3">
              <Bank size={28} weight="light" /> Wealth for Life Leads
            </h1>
            <p className="mt-2 text-exodus-text/70 text-sm">
              Every Blueprint submission from your Infinite Banking calculator. Auto-refreshes every 30 seconds.
            </p>
          </div>
          <div className="flex gap-3">
            {[
              ["Total leads", data.count, Users],
              ["Total submissions", data.total_submissions, TrendUp],
            ].map(([lbl, val, Ic]) => (
              <div key={lbl} className="rounded-xl border border-exodus-gold-deep/50 bg-exodus-panel px-4 py-3 min-w-[140px]">
                <Ic size={16} className="text-exodus-gold" />
                <p className="mt-1 text-[10px] tracking-wider uppercase text-exodus-text/60">{lbl}</p>
                <p className="mt-0.5 text-2xl font-bold text-exodus-gold">{val}</p>
              </div>
            ))}
          </div>
        </header>

        {/* Tier chips */}
        <section className="flex flex-wrap gap-2" data-testid="ibc-admin-tier-chips">
          {["t1","t2","t3","t4","t5"].map((t) => (
            <div key={t}
                 className="rounded-full border border-exodus-gold-deep/40 bg-exodus-surface px-3 py-1.5 text-xs flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{background: TIER_META[t].color}}/>
              <span className="text-exodus-text/70">{TIER_META[t].label}</span>
              <span className="text-exodus-gold font-bold">{data.by_tier?.[t] || 0}</span>
            </div>
          ))}
        </section>

        <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
          {/* Lead list */}
          <div className="rounded-2xl border border-exodus-gold-deep/50 bg-exodus-panel overflow-hidden">
            <div className="p-3 border-b border-exodus-gold-deep/40 flex items-center gap-2">
              <MagnifyingGlass size={16} className="text-exodus-text/50" />
              <input data-testid="ibc-admin-search"
                     placeholder="Search email, name, goal, status…"
                     value={q} onChange={(e) => setQ(e.target.value)}
                     className="bg-transparent outline-none text-sm text-exodus-text w-full" />
            </div>
            <div className="max-h-[70vh] overflow-y-auto divide-y divide-exodus-gold-deep/20">
              {loading ? (
                <p className="p-5 text-exodus-text/50 text-sm">Loading…</p>
              ) : filtered.length === 0 ? (
                <p className="p-5 text-exodus-text/50 text-sm">No leads yet. When a visitor submits the Blueprint calculator, they&apos;ll appear here.</p>
              ) : filtered.map((l) => {
                const rec = l.recommendation || {};
                const tier = TIER_META[rec.tier] || {label: rec.tier || "?", color: "#666"};
                const isSel = selected?.id === l.id;
                return (
                  <button key={l.id} data-testid={`ibc-lead-row-${l.id}`}
                          onClick={() => setSelected(l)}
                          className={`w-full text-left px-4 py-3 hover:bg-exodus-surface/60 transition ${isSel ? "bg-exodus-surface" : ""}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-exodus-text truncate">{l.name || l.email || "(no name)"}</p>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap"
                            style={{ background: tier.color+"22", color: tier.color }}>{tier.label}</span>
                    </div>
                    <p className="text-xs text-exodus-text/60 truncate">{l.email}</p>
                    <div className="mt-1 flex items-center gap-3 text-[11px] text-exodus-text/50">
                      <span>{l.status || "new"}</span>
                      <span>·</span>
                      <span>{l.submission_count || 1} submission{(l.submission_count||1)!==1?"s":""}</span>
                      <span>·</span>
                      <span>{fmt(l.updated_at || l.created_at)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Detail panel */}
          <div className="rounded-2xl border border-exodus-gold-deep/50 bg-exodus-panel p-6 min-h-[70vh]">
            {!selected ? (
              <p className="text-exodus-text/50">Select a lead on the left to see the full Blueprint history.</p>
            ) : (() => {
              const rec = selected.recommendation || {};
              const tier = TIER_META[rec.tier] || {label: rec.tier || "?"};
              return (
                <div data-testid="ibc-admin-detail" className="space-y-6">
                  <header className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="text-2xl font-bold text-exodus-gold">{selected.name || "(no name)"}</h2>
                      <p className="text-sm text-exodus-text/70">
                        <a href={`mailto:${selected.email}`} className="underline hover:text-exodus-gold">{selected.email}</a>
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs">
                        <span className="rounded-full px-2 py-1 bg-exodus-gold/15 text-exodus-gold font-bold">{tier.label}</span>
                        <span className="rounded-full px-2 py-1 bg-exodus-surface text-exodus-text/70">
                          {rec.capacity_score || "capacity: —"}
                        </span>
                        <span className="rounded-full px-2 py-1 bg-exodus-surface text-exodus-text/70">
                          Submissions: {selected.submission_count || 1}
                        </span>
                      </div>
                    </div>
                    <a data-testid="ibc-admin-email-btn"
                       href={`mailto:${selected.email}?subject=Your Wealth for Life Blueprint`}
                       className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-exodus-gold text-black font-bold text-sm hover:bg-exodus-gold/90">
                      <PaperPlaneTilt size={14} weight="fill" /> Email {selected.name?.split(" ")[0] || "them"}
                    </a>
                  </header>

                  {/* Editable status / notes */}
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-exodus-text/60">Status</label>
                      <select data-testid="ibc-admin-status"
                              value={selected.status || "new"}
                              onChange={(e) => saveField("status", e.target.value)}
                              className="mt-1 w-full rounded-lg border border-exodus-gold-deep/40 bg-exodus-surface px-3 py-2 text-exodus-text text-sm">
                        {STATUS_OPTS.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-exodus-text/60">Next action</label>
                      <input data-testid="ibc-admin-nextaction"
                             defaultValue={selected.next_action || ""}
                             onBlur={(e) => e.target.value !== (selected.next_action||"") && saveField("next_action", e.target.value)}
                             placeholder="e.g. call Tue 3pm"
                             className="mt-1 w-full rounded-lg border border-exodus-gold-deep/40 bg-exodus-surface px-3 py-2 text-exodus-text text-sm" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-[11px] uppercase tracking-wider text-exodus-text/60">Advisor notes</label>
                      <textarea data-testid="ibc-admin-notes"
                                rows={3}
                                defaultValue={selected.advisor_notes || ""}
                                onBlur={(e) => e.target.value !== (selected.advisor_notes||"") && saveField("advisor_notes", e.target.value)}
                                placeholder="Notes only you see. Autosaves on blur."
                                className="mt-1 w-full rounded-lg border border-exodus-gold-deep/40 bg-exodus-surface px-3 py-2 text-exodus-text text-sm resize-y" />
                    </div>
                  </div>

                  {/* Latest inputs snapshot */}
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-exodus-gold-deep font-semibold mb-2">Latest Blueprint inputs</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                      {[
                        ["Age",              rec.inputs?.age],
                        ["Income",           money(rec.inputs?.income)],
                        ["Monthly capacity", money(rec.inputs?.monthly_capacity)],
                        ["Married",          rec.inputs?.has_spouse ? "Yes" : "No"],
                        ["Kids",             rec.inputs?.has_kids ? "Yes" : "No"],
                        ["Primary goal",     rec.inputs?.primary_goal],
                      ].map(([k, v]) => (
                        <div key={k} className="rounded-lg bg-exodus-surface border border-exodus-gold-deep/30 p-3">
                          <p className="text-[10px] uppercase tracking-wider text-exodus-text/50">{k}</p>
                          <p className="mt-1 text-exodus-text text-sm">{v ?? "—"}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recommended stack */}
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-exodus-gold-deep font-semibold mb-2">Recommended policy stack</p>
                    <ol className="space-y-2">
                      {(rec.policy_stack || []).map((p, i) => (
                        <li key={i} className="rounded-lg bg-exodus-surface border border-exodus-gold-deep/30 p-3 flex gap-3">
                          <span className="text-exodus-gold font-bold text-lg w-6">{p.seq}.</span>
                          <div>
                            <p className="text-exodus-text text-sm font-bold">{p.type} <span className="text-exodus-text/60 font-normal">· {p.when}</span></p>
                            <p className="text-exodus-text/70 text-xs">Insured: {p.insured}</p>
                            <p className="text-exodus-text/60 text-xs italic mt-1">{p.role}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                    <div className="mt-3 text-xs text-exodus-text/60">
                      Split: <span className="text-exodus-gold font-semibold">
                        {rec.base_pua_split?.base_pct}% base / {rec.base_pua_split?.pua_pct}% PUA
                      </span> · Reservoir Yr20: <span className="text-exodus-gold">{rec.dynasty_projection?.year_20}</span>
                    </div>
                  </div>

                  {/* History timeline */}
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-exodus-gold-deep font-semibold mb-2 flex items-center gap-2">
                      <ClockCounterClockwise size={14} /> Submission history
                    </p>
                    <ul className="space-y-2">
                      {(selected.history || []).slice().reverse().map((h, i) => (
                        <li key={i} className="rounded-lg bg-exodus-surface/60 border border-exodus-gold-deep/20 p-3 text-xs">
                          <div className="flex items-center justify-between">
                            <span className="text-exodus-gold-deep">{fmt(h.at)}</span>
                            <span className="text-exodus-gold font-bold">{TIER_META[h.tier]?.label || h.tier}</span>
                          </div>
                          <p className="mt-1 text-exodus-text/70">
                            Income {money(h.inputs?.income)} · Capacity {money(h.inputs?.monthly_capacity)} ·
                            Goal: {h.inputs?.primary_goal}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
